import React from 'react';
import { Shield, Truck, Lock } from 'lucide-react';
import { formatMoney } from '../../utils/format';
import { isCoverageAllowed, minCoverageForTier } from '../../data/insurancePolicy';

const COVERAGE_TIERS = [
  { id: 'basic',    label: 'Basique',  mult: 0.70, cov: '60 %', color: 'text-warn',   accent: 'border-warn' },
  { id: 'standard', label: 'Standard', mult: 1.00, cov: '80 %', color: 'text-accent', accent: 'border-accent' },
  { id: 'premium',  label: 'Premium',  mult: 1.35, cov: '95 %', color: 'text-money',  accent: 'border-money' },
];

export function AssuranceTab({ vehicles, setInsuranceCoverage }) {
  const totalPremium = vehicles.reduce((s, v) => {
    const tier = v.insuranceCoverage || 'standard';
    const mult = tier === 'basic' ? 0.70 : tier === 'premium' ? 1.35 : 1;
    return s + (v.insurancePerMonth || 0) * mult;
  }, 0);
  const allClaims = vehicles.flatMap(v =>
    (v.insuranceClaims || []).map(c => ({ ...c, vehicleName: `${v.brand} ${v.model}` }))
  ).sort((a, b) => (b.day || 0) - (a.day || 0));
  const totalReimbursements = allClaims.reduce((s, c) => s + (c.reimbursement || 0), 0);

  return (
    <div className="space-y-4" data-testid="insurance-tab">
      <div className="bg-ink-800 rounded-lg border border-ink-600 p-4">
        <h3 className="text-sm font-semibold text-slate-200 mb-3 flex items-center gap-2">
          <Shield className="w-4 h-4 text-money" /> Synthèse assurance flotte
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
          <div className="bg-ink-700/30 rounded p-2.5 text-center border border-ink-600">
            <p className="text-[10px] text-slate-500 uppercase">Véhicules couverts</p>
            <p className="text-xl font-bold text-foreground" data-testid="insurance-vehicle-count">{vehicles.length}</p>
          </div>
          <div className="bg-ink-700/30 rounded p-2.5 text-center border border-ink-600">
            <p className="text-[10px] text-slate-500 uppercase">Prime mensuelle</p>
            <p className="text-xl font-bold text-danger" data-testid="insurance-total-premium">{formatMoney(Math.round(totalPremium))}</p>
          </div>
          <div className="bg-ink-700/30 rounded p-2.5 text-center border border-ink-600">
            <p className="text-[10px] text-slate-500 uppercase">Sinistres déclarés</p>
            <p className="text-xl font-bold text-warn" data-testid="insurance-claims-count">{allClaims.length}</p>
          </div>
          <div className="bg-ink-700/30 rounded p-2.5 text-center border border-ink-600">
            <p className="text-[10px] text-slate-500 uppercase">Remboursements cumulés</p>
            <p className="text-xl font-bold text-money" data-testid="insurance-reimb-total">{formatMoney(totalReimbursements)}</p>
          </div>
        </div>
        {allClaims.length > 0 && (
          <div className="mt-3">
            <p className="text-[11px] uppercase tracking-wider text-slate-500 mb-1.5">Historique des sinistres</p>
            <div className="max-h-40 overflow-y-auto space-y-1 pr-1">
              {allClaims.map((c, idx) => (
                <div key={`${c.id}-${idx}`} className="flex items-center justify-between bg-ink-700/30 border border-ink-600 rounded px-2.5 py-1.5 text-[11px]" data-testid={`insurance-claim-${idx}`}>
                  <div>
                    <p className="font-semibold text-slate-200">{c.vehicleName}</p>
                    <p className="text-[10px] text-slate-500">J{c.day} · sinistre #{c.claimNumber} · couv. {Math.round((c.coverageRate || 0) * 100)} %</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-money font-mono">+{formatMoney(c.reimbursement)}</p>
                    <p className="text-[10px] text-slate-500">franchise {formatMoney(c.franchise)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="bg-ink-800 rounded-lg border border-ink-600 p-4">
        <h3 className="text-sm font-semibold text-slate-200 mb-3 flex items-center gap-2">
          <Truck className="w-4 h-4 text-accent" /> Couverture par véhicule
        </h3>
        <div className="space-y-2">
          {vehicles.length === 0 && (
            <p className="text-xs text-slate-500 italic text-center py-4">Aucun véhicule dans la flotte.</p>
          )}
          {vehicles.map(v => {
            const tier = v.insuranceCoverage || 'standard';
            return (
              <div key={v.id} className="border border-ink-600 bg-ink-700/30 rounded-lg p-3" data-testid={`insurance-row-${v.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="text-xs font-semibold text-slate-200">{v.brand} {v.model}</p>
                    <p className="text-[10px] text-slate-500">Tier {v.tier} · prime de base {formatMoney(v.insurancePerMonth || 0)}/m</p>
                  </div>
                  <p className="text-xs font-bold text-danger font-mono">
                    {formatMoney(Math.round((v.insurancePerMonth || 0) * (tier === 'basic' ? 0.70 : tier === 'premium' ? 1.35 : 1)))}/m
                  </p>
                </div>
                <div className="grid grid-cols-3 gap-1.5">
                  {COVERAGE_TIERS.map(opt => {
                    const locked = !isCoverageAllowed(v.tier, opt.id);
                    return (
                      <button
                        key={opt.id}
                        onClick={() => !locked && setInsuranceCoverage(v.id, opt.id)}
                        disabled={locked}
                        title={locked
                          ? `Couverture minimale exigée pour un Tier ${v.tier} : ${minCoverageForTier(v.tier) === 'premium' ? 'Premium' : 'Standard'} (exigence des assureurs)`
                          : undefined}
                        className={`text-[10px] px-2 py-1.5 rounded font-mono uppercase tracking-wider border transition-all ${
                          locked
                            ? 'border-ink-700 text-slate-600 opacity-50 cursor-not-allowed'
                            : tier === opt.id
                              ? `${opt.accent} ${opt.color} bg-ink-900`
                              : 'border-ink-600 text-slate-400 hover:border-ink-500'
                        }`}
                        data-testid={`coverage-${v.id}-${opt.id}`}
                      >
                        <span className="block font-bold flex items-center justify-center gap-1">
                          {locked && <Lock className="w-2.5 h-2.5 inline" />}{opt.label}
                        </span>
                        <span className="block text-[10px] opacity-80">
                          x{opt.mult.toFixed(2)} · {opt.cov}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
        <div className="mt-3 text-[10px] text-slate-500 leading-snug bg-ink-700/20 rounded p-2 border border-ink-600">
          <p><span className="text-warn font-bold">Basique</span> : -30 % prime, 60 % de remboursement sinistre.</p>
          <p><span className="text-accent font-bold">Standard</span> : prime nominale, 80 % de remboursement.</p>
          <p><span className="text-money font-bold">Premium</span> : +35 % prime, 95 % de remboursement, les 2 premiers sinistres sans pénalité.</p>
          <p className="mt-1 text-warn/90"><span className="font-bold">Exigences assureurs</span> : Tier 3-4 → Standard minimum · Tier 5 → Premium obligatoire. Les aléas majeurs (rupture moteur, vol de camion, vol de marchandise) sont indemnisés selon la couverture.</p>
          <p className="mt-1 text-money/90"><span className="font-bold">Bonus banque</span> : flotte 100 % Premium = +5 points au score crédit (jusqu'à proportionnel).</p>
        </div>
      </div>
    </div>
  );
}
